import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Check, ArrowUpRight, Upload, Briefcase, Clock, GraduationCap, Truck, FileText, X, MapPin, Euro, CalendarDays, Sparkles } from "lucide-react";
import Reveal from "./Reveal";
import { gtmEvent } from "@/lib/gtm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPES = [
  {
    value: "vast",
    label: "Vaste functie",
    description: "Voltijds bij Cargides aan de slag",
    icon: Briefcase,
  },
  {
    value: "flexi",
    label: "Flexi-job",
    description: "Bijverdienen naast je hoofdjob",
    icon: Clock,
  },
  {
    value: "student",
    label: "Student",
    description: "Werken tijdens schoolvakanties of weekend",
    icon: GraduationCap,
  },
  {
    value: "onderaannemer",
    label: "Onderaannemer",
    description: "Samenwerken als zelfstandige",
    icon: Truck,
  },
];

const initialForm = {
  naam: "",
  email: "",
  telefoon: "",
  type_job: [],
  motivatie: "",
};

export default function Jobs() {
  const [form, setForm] = useState(initialForm);
  const [cv, setCv] = useState(null);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [vacancies, setVacancies] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vacancies`);
        setVacancies(Array.isArray(data) ? data : []);
      } catch {
        setVacancies([]);
      }
    })();
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleType = (val) => {
    setForm((f) => ({
      ...f,
      type_job: f.type_job.includes(val) ? f.type_job.filter((v) => v !== val) : [...f.type_job, val],
    }));
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setStatus({ state: "error", message: "CV is te groot (max 6 MB)." });
      return;
    }
    setCv(file);
    setStatus({ state: "idle", message: "" });
  };

  const removeCv = () => {
    setCv(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const applyToVacancy = (vacancy) => {
    gtmEvent("vacancy_apply_click", { vacancy: vacancy?.title || "unknown", type: vacancy?.type_job });
    if (vacancy?.type_job) {
      setForm((f) => ({
        ...f,
        type_job: f.type_job.includes(vacancy.type_job) ? f.type_job : [...f.type_job, vacancy.type_job],
      }));
    }
    setTimeout(() => {
      document.querySelector("[data-testid='jobs-form']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      document.querySelector("[data-testid='jobs-input-naam']")?.focus();
    }, 200);
  };

  const iconForType = (t) => {
    if (t === "vast") return Briefcase;
    if (t === "flexi") return Clock;
    if (t === "student") return GraduationCap;
    if (t === "onderaannemer") return Truck;
    return Briefcase;
  };
  const labelForType = (t) => JOB_TYPES.find((x) => x.value === t)?.label || t;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.email || !form.telefoon || form.type_job.length === 0) {
      setStatus({ state: "error", message: "Vul alle verplichte velden in (selecteer minstens één type)." });
      return;
    }
    setStatus({ state: "loading", message: "" });

    try {
      const fd = new FormData();
      fd.append("naam", form.naam);
      fd.append("email", form.email);
      fd.append("telefoon", form.telefoon);
      fd.append("type_job", form.type_job.join(","));
      fd.append("motivatie", form.motivatie);
      if (cv) fd.append("cv", cv);

      const { data } = await axios.post(`${API}/jobs`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      gtmEvent("form_submit_success", {
        form_name: "sollicitatie",
        job_types: form.type_job.join(","),
        has_cv: !!cv,
      });
      setStatus({ state: "success", message: data?.message || "Bedankt voor je sollicitatie!" });
      setForm(initialForm);
      removeCv();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Er ging iets mis. Probeer het later nog eens.";
      gtmEvent("form_submit_error", { form_name: "sollicitatie", error: msg.slice(0, 100) });
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <section id="jobs" data-testid="jobs-section" className="relative bg-cgsurface py-24 md:py-36 overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cgprimary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Open vacatures (dynamic from API) */}
        {vacancies.length > 0 && (
          <div className="mb-16 md:mb-24 space-y-6" data-testid="job-vacancies-list">
            {vacancies.map((v) => {
              const TypeIcon = iconForType(v.type_job);
              return (
                <Reveal key={v.id} data-testid={`job-vacancy-${v.id}`}>
                  <div className="relative bg-cgdark text-white overflow-hidden">
                    <div className="absolute inset-0 grain opacity-15" />
                    <div className="absolute top-0 right-0 w-96 h-96 bg-cgprimary/15 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />

                    <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 p-8 md:p-12 lg:p-16">
                      <div className="lg:col-span-5">
                        <div className="inline-flex items-center gap-2 bg-cgprimary text-white px-3 py-1.5 mb-6 text-[10px] font-bold tracking-[0.2em] uppercase">
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          Open vacature · {labelForType(v.type_job)}
                        </div>
                        <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] mb-6">
                          {v.title}
                          {v.subtitle && (
                            <>
                              <br />
                              <span className="text-cgprimary italic font-light">{v.subtitle}</span>
                            </>
                          )}
                        </h3>
                        <p className="text-white/70 text-base leading-relaxed mb-8">{v.description}</p>
                        <button
                          type="button"
                          onClick={() => applyToVacancy(v)}
                          className="inline-flex items-center gap-3 bg-cgprimary hover:bg-cgprimaryhover text-white px-6 py-4 text-[12px] font-semibold tracking-[0.15em] uppercase transition-all duration-300 hover:-translate-y-0.5"
                          data-testid={`vacancy-apply-button-${v.id}`}
                        >
                          Solliciteer voor deze job <ArrowUpRight size={16} />
                        </button>
                      </div>

                      <div className="lg:col-span-7 flex flex-col gap-px bg-white/10 self-start w-full">
                        {v.highlights && v.highlights.length > 0 && (
                          <div className={`grid ${v.highlights.length > 1 ? "sm:grid-cols-2" : ""} gap-px bg-white/10`}>
                            {v.highlights.map((h, i) => (
                              <div key={i} className="bg-cgdark p-6">
                                <TypeIcon size={20} className="text-cgprimary mb-3" strokeWidth={1.5} />
                                <div className="font-heading text-lg font-bold text-white leading-tight">{h}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {v.tasks && v.tasks.length > 0 && (
                          <div className="bg-cgdark p-6">
                            <Sparkles size={20} className="text-cgprimary mb-3" strokeWidth={1.5} />
                            <div className="text-white/50 text-[10px] uppercase tracking-[0.2em] mb-3">Wat doe je?</div>
                            <ul className="text-white/85 text-sm space-y-1.5 leading-relaxed">
                              {v.tasks.map((t, i) => (
                                <li key={i} className="flex gap-2">
                                  <span className="text-cgprimary flex-shrink-0">·</span>
                                  {t}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow mb-5" data-testid="jobs-eyebrow">Ook interesse?</p>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-cgdark mb-8">
                Werk je <br />
                <span className="italic font-light">graag met</span> <br />
                <span className="text-cgprimary">ons mee?</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="text-cgmuted text-base md:text-lg leading-relaxed mb-8">
                Ook wanneer er geen open vacature staat: ons team groeit elk jaar. Reageer op de vacature hierboven of stuur ons vrijblijvend je gegevens voor een flexi-job, studentenjob of onderaanneming.
              </p>
              <p className="text-cgmuted text-base md:text-lg leading-relaxed mb-10">
                We bekijken elke aanvraag persoonlijk en contacteren je zodra er een kans openvalt.
              </p>
            </Reveal>

            <Reveal delay={180} className="bg-white border border-cgborder p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-cgprimary font-semibold mb-3">
                Heb je vragen?
              </div>
              <div className="font-heading text-lg font-semibold text-cgdark mb-1">Justine Verbeke</div>
              <div className="text-cgmuted text-sm mb-3">Sales & Operations</div>
              <a
                href="tel:+32490265588"
                className="text-cgdark hover:text-cgprimary transition-colors font-semibold text-sm link-underline"
                data-testid="jobs-contact-phone"
              >
                +32 490 26 55 88
              </a>
            </Reveal>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-white border border-cgborder p-6 md:p-10">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-cgdark mb-2">
                  Solliciteer vrijblijvend
                </h3>
                <p className="text-cgmuted text-sm mb-8">
                  Alle velden met * zijn verplicht. Een CV is welkom maar niet verplicht.
                </p>

                <form onSubmit={submit} data-testid="jobs-form" className="space-y-1">
                  {/* Job type selection */}
                  <div className="mb-8">
                    <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mb-4">
                      Ik solliciteer als * <span className="normal-case tracking-normal text-cgmuted/70 font-normal">(meerdere mogelijk)</span>
                    </label>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="jobs-type-group">
                      {JOB_TYPES.map((t) => {
                        const Icon = t.icon;
                        const checked = form.type_job.includes(t.value);
                        return (
                          <button
                            type="button"
                            key={t.value}
                            onClick={() => toggleType(t.value)}
                            data-testid={`jobs-type-${t.value}`}
                            className={`group flex items-start gap-3 border p-4 text-left transition-all duration-300 ${
                              checked ? "border-cgprimary bg-cgprimary/5" : "border-cgborder hover:border-cgdark"
                            }`}
                          >
                            <div
                              className={`w-10 h-10 flex items-center justify-center border transition-colors flex-shrink-0 ${
                                checked
                                  ? "bg-cgprimary border-cgprimary text-white"
                                  : "border-cgborder text-cgdark"
                              }`}
                            >
                              {checked ? <Check size={18} strokeWidth={2.5} /> : <Icon size={18} strokeWidth={1.5} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[14px] text-cgdark">{t.label}</div>
                              <div className="text-cgmuted text-xs mt-0.5 leading-snug">{t.description}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-x-8">
                    <div>
                      <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                        Naam *
                      </label>
                      <input
                        className="cg-input"
                        value={form.naam}
                        onChange={update("naam")}
                        required
                        data-testid="jobs-input-naam"
                        placeholder="Voor- en achternaam"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                        Telefoon *
                      </label>
                      <input
                        className="cg-input"
                        value={form.telefoon}
                        onChange={update("telefoon")}
                        required
                        data-testid="jobs-input-telefoon"
                        placeholder="+32 ..."
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-4">
                        E-mail *
                      </label>
                      <input
                        className="cg-input"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        required
                        data-testid="jobs-input-email"
                        placeholder="jij@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mt-6">
                      Motivatie / extra info
                    </label>
                    <textarea
                      className="cg-input min-h-[100px] resize-y"
                      value={form.motivatie}
                      onChange={update("motivatie")}
                      rows={4}
                      data-testid="jobs-input-motivatie"
                      placeholder="Vertel kort wie je bent, je beschikbaarheid, ervaring, ..."
                    />
                  </div>

                  {/* CV Upload */}
                  <div className="mt-8">
                    <label className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-cgmuted mb-3">
                      CV (optioneel)
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                      onChange={onFileChange}
                      className="hidden"
                      data-testid="jobs-input-cv"
                    />
                    {!cv ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-3 border border-dashed border-cgborder hover:border-cgprimary hover:bg-cgprimary/5 transition-all duration-300 px-6 py-6 text-cgmuted hover:text-cgdark"
                        data-testid="jobs-upload-button"
                      >
                        <Upload size={20} className="text-cgprimary" />
                        <span className="text-sm font-semibold">
                          CV uploaden (PDF, DOC, DOCX, JPG, PNG · max 6 MB)
                        </span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-4 border border-cgprimary bg-cgprimary/5 px-4 py-3">
                        <FileText size={20} className="text-cgprimary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-cgdark truncate" data-testid="jobs-cv-name">
                            {cv.name}
                          </div>
                          <div className="text-xs text-cgmuted">{(cv.size / 1024).toFixed(0)} KB</div>
                        </div>
                        <button
                          type="button"
                          onClick={removeCv}
                          className="w-8 h-8 flex items-center justify-center text-cgdark hover:text-cgprimary"
                          data-testid="jobs-cv-remove"
                          aria-label="Verwijder CV"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <p className="text-xs text-cgmuted max-w-xs">
                      Door dit formulier te versturen ga je akkoord met ons privacybeleid.
                    </p>
                    <button
                      type="submit"
                      className="cg-btn-primary"
                      disabled={status.state === "loading"}
                      data-testid="jobs-submit-button"
                    >
                      {status.state === "loading" ? "Versturen..." : "Verstuur sollicitatie"}
                      <ArrowUpRight size={16} />
                    </button>
                  </div>

                  {status.state === "success" && (
                    <div
                      className="mt-8 border-l-2 border-cgprimary bg-cgsurface p-6 flex items-start gap-3"
                      data-testid="jobs-success-message"
                    >
                      <Check size={20} className="text-cgprimary flex-shrink-0 mt-0.5" />
                      <p className="text-cgdark text-sm leading-relaxed">{status.message}</p>
                    </div>
                  )}
                  {status.state === "error" && (
                    <div
                      className="mt-8 border-l-2 border-red-500 bg-red-50 p-6"
                      data-testid="jobs-error-message"
                    >
                      <p className="text-red-700 text-sm leading-relaxed">{status.message}</p>
                    </div>
                  )}
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
